/**
 * EmployeeDocumentsSection
 *
 * Renders the twenty-three fixed employee document slots on the `EmployeeAccountingEdit`
 * page: the original 5 identity/onboarding slots, 7 additional optional
 * onboarding and offboarding slots, 10 optional proof-of-dependents slots, and 1
 * optional termination-decision slot.
 * Each slot displays:
 *   - The document label, shown in both English and Vietnamese
 *   - Current filename + size (when a file is stored)
 *   - A file picker to upload or replace the document
 *   - View, Download, and Delete action buttons (when a file exists)
 *
 * The **View** button opens {@link DocumentViewerDialog} which renders the
 * document inline — images are displayed with an `<img>` tag; PDFs are rendered
 * in an `<iframe>`.
 *
 * Upload, download, and delete use the custom dataProvider helpers
 * (`listEmployeeDocuments`, `uploadEmployeeDocument`, `downloadEmployeeDocument`,
 * `deleteEmployeeDocument`).
 *
 * Permission-based hiding:
 *   - Upload (replace) and delete controls are hidden when the user lacks the
 *     `employees.manage` permission. This is determined once via the
 *     `checkPermission` helper from `dataProvider.ts`.
 *
 * @module employees/EmployeeDocumentsSection
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    listEmployeeDocuments,
    uploadEmployeeDocument,
    downloadEmployeeDocument,
    deleteEmployeeDocument,
    checkPermission,
} from '../dataProvider';
import DocumentViewerDialog from './DocumentViewerDialog';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The twenty-three fixed document-slot identifiers (must match the backend `DocType`). */
export type DocType =
    | 'id_card'
    | 'health_insurance_book'
    | 'health_insurance_card'
    | 'vneid_record'
    | 'cv_certified'
    | 'resignation_letter'
    | 'resignation_settlement'
    | 'asset_return'
    | 'post_employment_confidentiality'
    | 'payment_policy_agreement'
    | 'probation_contract'
    | 'asset_handover'
    | 'dependent_proof_1'
    | 'dependent_proof_2'
    | 'dependent_proof_3'
    | 'dependent_proof_4'
    | 'dependent_proof_5'
    | 'dependent_proof_6'
    | 'dependent_proof_7'
    | 'dependent_proof_8'
    | 'dependent_proof_9'
    | 'dependent_proof_10'
    | 'termination_decision';

/** A document slot's title in both English and Vietnamese. */
interface DocTypeLabel {
    /** English title. */
    en: string;
    /** Vietnamese title. */
    vi: string;
}

/** Human-readable English + Vietnamese titles for each document slot. */
const DOC_TYPE_LABELS: Record<DocType, DocTypeLabel> = {
    id_card: { en: 'ID Card / CCCD', vi: 'CCCD' },
    health_insurance_book: { en: 'Health Insurance Book Cover', vi: 'Bìa sổ bảo hiểm y tế' },
    health_insurance_card: { en: 'Health Insurance Card', vi: 'Thẻ bảo hiểm y tế' },
    vneid_record: { en: 'VNeID Verified Electronic Record', vi: 'Hồ sơ điện tử xác thực VNeID' },
    cv_certified: { en: 'Locally-Certified Curriculum Vitae', vi: 'Sơ yếu lý lịch có chứng thực' },
    resignation_letter: { en: 'Resignation Letter', vi: 'Đơn xin nghỉ việc' },
    resignation_settlement: { en: 'Resignation Settlement Record', vi: 'Biên bản thanh lý nghỉ việc' },
    asset_return: { en: 'Asset Return Record', vi: 'Biên bản hoàn trả tài sản' },
    post_employment_confidentiality: {
        en: 'Post-Employment Confidentiality Commitment',
        vi: 'Cam kết bảo mật thông tin sau khi nghỉ việc',
    },
    payment_policy_agreement: { en: 'Payment Policy Agreement', vi: 'Thỏa thuận chính sách thanh toán' },
    probation_contract: { en: 'Probation/Collaborator Contract', vi: 'Hợp đồng thử việc-CTV' },
    asset_handover: { en: 'Asset Handover', vi: 'Bàn giao tài sản' },
    dependent_proof_1: { en: 'Proof of Dependent 1', vi: 'Chứng từ người phụ thuộc 1' },
    dependent_proof_2: { en: 'Proof of Dependent 2', vi: 'Chứng từ người phụ thuộc 2' },
    dependent_proof_3: { en: 'Proof of Dependent 3', vi: 'Chứng từ người phụ thuộc 3' },
    dependent_proof_4: { en: 'Proof of Dependent 4', vi: 'Chứng từ người phụ thuộc 4' },
    dependent_proof_5: { en: 'Proof of Dependent 5', vi: 'Chứng từ người phụ thuộc 5' },
    dependent_proof_6: { en: 'Proof of Dependent 6', vi: 'Chứng từ người phụ thuộc 6' },
    dependent_proof_7: { en: 'Proof of Dependent 7', vi: 'Chứng từ người phụ thuộc 7' },
    dependent_proof_8: { en: 'Proof of Dependent 8', vi: 'Chứng từ người phụ thuộc 8' },
    dependent_proof_9: { en: 'Proof of Dependent 9', vi: 'Chứng từ người phụ thuộc 9' },
    dependent_proof_10: { en: 'Proof of Dependent 10', vi: 'Chứng từ người phụ thuộc 10' },
    termination_decision: {
        en: 'Decision on Termination of Labor Contract',
        vi: 'Quyết định chấm dứt hợp đồng lao động',
    },
};

/**
 * Formats a slot's EN + VN titles into a single display/accessibility string.
 *
 * Accepts an already-resolved {@link DocTypeLabel} (rather than a `DocType`) so
 * callers that have already looked up `DOC_TYPE_LABELS[docType]` for rendering
 * can reuse that same lookup instead of hitting the map twice per render.
 *
 * @param label - The slot's EN + VN titles.
 * @returns A string of the form `"English Title / Vietnamese Title"`.
 */
function formatLabel (label: DocTypeLabel): string {
    return `${label.en} / ${label.vi}`;
}

/** Whether each slot is required. Optional slots may be left empty. */
const DOC_TYPE_REQUIRED: Record<DocType, boolean> = {
    id_card: true,
    health_insurance_book: false,
    health_insurance_card: false,
    vneid_record: true,
    cv_certified: true,
    resignation_letter: false,
    resignation_settlement: false,
    asset_return: false,
    post_employment_confidentiality: false,
    payment_policy_agreement: false,
    probation_contract: false,
    asset_handover: false,
    dependent_proof_1: false,
    dependent_proof_2: false,
    dependent_proof_3: false,
    dependent_proof_4: false,
    dependent_proof_5: false,
    dependent_proof_6: false,
    dependent_proof_7: false,
    dependent_proof_8: false,
    dependent_proof_9: false,
    dependent_proof_10: false,
    termination_decision: false,
};

/** Ordered list of slot identifiers for consistent rendering order. */
const DOC_TYPES: DocType[] = [
    'id_card',
    'health_insurance_book',
    'health_insurance_card',
    'vneid_record',
    'cv_certified',
    'resignation_letter',
    'resignation_settlement',
    'asset_return',
    'post_employment_confidentiality',
    'payment_policy_agreement',
    'probation_contract',
    'asset_handover',
    'dependent_proof_1',
    'dependent_proof_2',
    'dependent_proof_3',
    'dependent_proof_4',
    'dependent_proof_5',
    'dependent_proof_6',
    'dependent_proof_7',
    'dependent_proof_8',
    'dependent_proof_9',
    'dependent_proof_10',
    'termination_decision',
];

/**
 * Metadata for a single document slot as returned by the list endpoint.
 * `null` indicates the slot is empty.
 */
export interface DocumentMeta {
    id: number;
    employee_id: string;
    doc_type: DocType;
    r2_key: string;
    original_filename: string;
    mime_type: string;
    size_bytes: number;
    uploaded_by: string;
    uploaded_at: string;
}

export type DocumentsMap = Record<DocType, DocumentMeta | null>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a byte count as a human-readable string (KB or MB).
 *
 * @param bytes - Raw byte count.
 * @returns Formatted string such as "12.3 KB" or "4.5 MB".
 */
function formatBytes (bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// EmployeeDocumentsSection
// ---------------------------------------------------------------------------

interface EmployeeDocumentsSectionProps {
    /**
     * The numeric `id` of the `employees` row.
     * Used to build the `/employees/:id/documents` API path.
     */
    accountingId: number | string;
}

/**
 * Displays and manages the five fixed employee document slots.
 *
 * Loads existing document metadata on mount. Uploads are handled inline via
 * a hidden `<input type="file">` per slot; delete requires a confirmation
 * click on the delete icon.
 *
 * @param props.accountingId - The numeric id of the employee_accounting row.
 */
const EmployeeDocumentsSection: React.FC<EmployeeDocumentsSectionProps> = ({
    accountingId,
}) => {
    const notify = useNotify();
    const [documents, setDocuments] = useState<DocumentsMap | null>(null);
    const [canManage, setCanManage] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState<Set<DocType>>(new Set());
    const [isFetching, setIsFetching] = useState(true);
    const [viewerDocType, setViewerDocType] = useState<DocType | null>(null);

    // Fetch initial document list and permission
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const [docs, manage] = await Promise.all([
                    listEmployeeDocuments(accountingId),
                    checkPermission('employees.manage'),
                ]);
                if (!cancelled) {
                    setDocuments(docs as DocumentsMap);
                    setCanManage(manage);
                }
            } catch {
                if (!cancelled) {
                    notify('Failed to load documents', { type: 'error' });
                }
            } finally {
                if (!cancelled) setIsFetching(false);
            }
        };
        void load();
        return () => { cancelled = true; };
    }, [accountingId, notify]);

    const setSlotLoading = (docType: DocType, loading: boolean) => {
        setLoadingSlots(prev => {
            const next = new Set(prev);
            if (loading) next.add(docType);
            else next.delete(docType);
            return next;
        });
    };

    const handleUpload = useCallback(
        async (docType: DocType, file: File) => {
            setSlotLoading(docType, true);
            try {
                const meta = await uploadEmployeeDocument(accountingId, docType, file);
                setDocuments(prev => ({ ...prev!, [docType]: meta }));
                notify('Document uploaded successfully', { type: 'success' });
            } catch {
                notify('Upload failed', { type: 'error' });
            } finally {
                setSlotLoading(docType, false);
            }
        },
        [accountingId, notify]
    );

    const handleDownload = useCallback(
        async (docType: DocType) => {
            setSlotLoading(docType, true);
            try {
                await downloadEmployeeDocument(accountingId, docType);
            } catch {
                notify('Download failed', { type: 'error' });
            } finally {
                setSlotLoading(docType, false);
            }
        },
        [accountingId, notify]
    );

    const handleDelete = useCallback(
        async (docType: DocType) => {
            if (!window.confirm(`Delete the "${formatLabel(DOC_TYPE_LABELS[docType])}" document? This cannot be undone.`)) {
                return;
            }
            setSlotLoading(docType, true);
            try {
                await deleteEmployeeDocument(accountingId, docType);
                setDocuments(prev => ({ ...prev!, [docType]: null }));
                notify('Document deleted', { type: 'success' });
            } catch {
                notify('Delete failed', { type: 'error' });
            } finally {
                setSlotLoading(docType, false);
            }
        },
        [accountingId, notify]
    );

    if (isFetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    const viewerMeta = viewerDocType ? (documents?.[viewerDocType] ?? null) : null;

    return (
        <Box data-testid="employee-documents-section" sx={{ width: '100%' }}>
            {DOC_TYPES.map(docType => (
                <DocumentSlotRowWithUpload
                    key={docType}
                    docType={docType}
                    meta={documents?.[docType] ?? null}
                    canManage={canManage}
                    onDeleted={handleDelete}
                    onDownload={handleDownload}
                    onView={setViewerDocType}
                    isLoading={loadingSlots.has(docType)}
                    onFileSelected={handleUpload}
                />
            ))}

            {/* Document viewer dialog — opens on View button click */}
            {viewerDocType && viewerMeta && (
                <DocumentViewerDialog
                    open={viewerDocType !== null}
                    onClose={() => setViewerDocType(null)}
                    accountingId={accountingId}
                    docType={viewerDocType}
                    label={formatLabel(DOC_TYPE_LABELS[viewerDocType])}
                />
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// DocumentSlotRowWithUpload
// Extends DocumentSlotRow to wire actual file → upload call.
// ---------------------------------------------------------------------------

interface DocumentSlotRowWithUploadProps {
    docType: DocType;
    meta: DocumentMeta | null;
    canManage: boolean;
    onDeleted: (docType: DocType) => void;
    onDownload: (docType: DocType) => void;
    /** Called when the user clicks the View button to open the inline viewer. */
    onView: (docType: DocType) => void;
    isLoading: boolean;
    onFileSelected: (docType: DocType, file: File) => void;
}

/**
 * Thin wrapper over `DocumentSlotRow` that converts the file-input change event
 * into an actual upload call by passing the `File` to `onFileSelected`.
 */
const DocumentSlotRowWithUpload: React.FC<DocumentSlotRowWithUploadProps> = ({
    docType,
    meta,
    canManage,
    onDeleted,
    onDownload,
    onView,
    isLoading,
    onFileSelected,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const docTypeLabel = DOC_TYPE_LABELS[docType];
    const { en: labelEn, vi: labelVi } = docTypeLabel;
    const label = formatLabel(docTypeLabel);
    const required = DOC_TYPE_REQUIRED[docType];

    return (
        <Box
            data-testid={`doc-slot-${docType}`}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
            }}
        >
            {/* Label — shown in both English and Vietnamese */}
            <Box sx={{ minWidth: 260 }} data-testid={`doc-label-${docType}`}>
                <Typography variant="body2" component="div">
                    {labelEn}
                    {required && (
                        <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                    )}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    {labelVi}
                </Typography>
            </Box>

            {/* File info */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
                {meta ? (
                    <Typography variant="body2" color="text.secondary" data-testid={`doc-filename-${docType}`}>
                        {meta.original_filename} ({formatBytes(meta.size_bytes)})
                    </Typography>
                ) : (
                    <Typography variant="body2" color="text.disabled" data-testid={`doc-empty-${docType}`}>
                        No file uploaded
                    </Typography>
                )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {isLoading && <CircularProgress size={20} data-testid={`doc-loading-${docType}`} />}

                {meta && (
                    <Tooltip title="View">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => onView(docType)}
                                data-testid={`doc-view-${docType}`}
                                disabled={isLoading}
                                aria-label={`View ${label}`}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {meta && (
                    <Tooltip title="Download">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => onDownload(docType)}
                                data-testid={`doc-download-${docType}`}
                                disabled={isLoading}
                                aria-label={`Download ${label}`}
                            >
                                <DownloadIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {canManage && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,application/pdf"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    onFileSelected(docType, file);
                                    e.target.value = '';
                                }
                            }}
                            data-testid={`doc-file-input-${docType}`}
                        />
                        <Tooltip title={meta ? 'Replace' : 'Upload'}>
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<UploadIcon />}
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isLoading}
                                    data-testid={`doc-upload-btn-${docType}`}
                                    aria-label={meta ? `Replace ${label}` : `Upload ${label}`}
                                >
                                    {meta ? 'Replace' : 'Upload'}
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}

                {canManage && meta && (
                    <Tooltip title="Delete">
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDeleted(docType)}
                                disabled={isLoading}
                                data-testid={`doc-delete-${docType}`}
                                aria-label={`Delete ${label}`}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

export default EmployeeDocumentsSection;
